/**
 * Add-ons page (iter-12a)
 *
 * Vendor-facing settings surface that lists every paid add-on the platform
 * currently exposes and lets the vendor toggle activation on their own row.
 *
 * Two add-ons are live in seed:
 *   - ai_booking_agent      Track 1 chatbot on the supplier's own site
 *   - booking_widget_embed  Track 3 hosted-storefront embed
 *
 * The catalogue always renders in full. Activation state is per-vendor via
 * GET /vendors/{vendorId}/add-ons. Activate/cancel go through the audit-wrapped
 * POST endpoints.
 *
 * Voice: "activate" and "cancel" — never "premium", "upgrade", or "unlock".
 * The base platform is not lesser. These are extra products on top.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

interface AddOn {
  slug: string
  name: string
  description: string | null
  tier_required: number
  billing_type: 'flat' | 'metered' | 'hybrid'
  status: 'active' | 'cancelled' | null
  activated_at: string | null
  cancelled_at: string | null
  is_active: boolean
}

const TIER_LABEL: Record<number, string> = {
  0: 'Free',
  1: 'Growth',
  2: 'Pro',
  3: 'Infrastructure',
}

export default function AddOnsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-add-ons', vendorId],
    queryFn: () => api.addOns.getForVendor(vendorId!),
    enabled: !!vendorId,
    staleTime: 60_000,
  })

  const activate = useMutation({
    mutationFn: (slug: string) => api.addOns.activate(vendorId!, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-add-ons', vendorId] })
    },
  })

  const cancel = useMutation({
    mutationFn: (slug: string) => api.addOns.cancel(vendorId!, slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-add-ons', vendorId] })
    },
  })

  const addOns: AddOn[] = (data?.data as any)?.addOns ?? []

  if (!vendorId) {
    return (
      <div style={{ padding: '2rem', maxWidth: 720, margin: '0 auto' }}>
        <p>Sign in as a vendor to manage add-ons.</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ marginBottom: '0.5rem' }}>Add-ons</h1>
      <p style={{ color: 'var(--color-gray-500)', marginBottom: '2rem', lineHeight: 1.6 }}>
        The Tixxly base platform is free. Two optional paid add-ons sit on top:
        the AI Website Booking Agent and the embeddable booking widget. Activate
        or cancel per account.
      </p>

      {isLoading && <p>Loading add-ons...</p>}
      {error && (
        <div style={{ color: 'var(--color-coral-red)', marginBottom: '1rem' }}>
          Failed to load add-ons. Try again in a moment.
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {addOns.map((addOn) => (
          <div
            key={addOn.slug}
            className="card"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '1.5rem',
              border: '1px solid var(--color-gray-200)',
              borderRadius: 8,
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.5rem',
                }}
              >
                <h3 style={{ margin: 0 }}>{addOn.name}</h3>
                {addOn.is_active ? (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: 'var(--color-lime-green, #d4f5b8)',
                      color: 'var(--color-forest-green, #2d5f1a)',
                      padding: '0.2rem 0.6rem',
                      borderRadius: 4,
                    }}
                  >
                    Active
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: 'var(--color-gray-100)',
                      color: 'var(--color-gray-500)',
                      padding: '0.2rem 0.6rem',
                      borderRadius: 4,
                    }}
                  >
                    {addOn.status === 'cancelled' ? 'Cancelled' : 'Inactive'}
                  </span>
                )}
              </div>
              <p
                style={{
                  margin: '0 0 0.5rem',
                  color: 'var(--color-gray-600)',
                  lineHeight: 1.5,
                }}
              >
                {addOn.description}
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-gray-500)' }}>
                Requires the {TIER_LABEL[addOn.tier_required] ?? 'Growth'} plan or above.
              </p>
            </div>

            <div>
              {addOn.is_active ? (
                <button
                  type="button"
                  onClick={() => cancel.mutate(addOn.slug)}
                  disabled={cancel.isPending}
                  style={{
                    padding: '0.6rem 1.2rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    background: 'var(--color-pure-white)',
                    color: 'var(--color-coral-red)',
                    border: '1px solid var(--color-coral-red)',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  {cancel.isPending ? 'Cancelling...' : 'Cancel'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => activate.mutate(addOn.slug)}
                  disabled={activate.isPending}
                  style={{
                    padding: '0.6rem 1.2rem',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    background: 'var(--color-tixxly-navy)',
                    color: 'var(--color-pure-white)',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  {activate.isPending ? 'Activating...' : 'Activate'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
